import { Box, Grid, Stack, Typography, Chip, ClickAwayListener, Grow, IconButton, Paper, Popper, Popover, Checkbox, TextField, Button, CircularProgress, Tooltip } from "@mui/material";
import NestedTableComponent from "components/NestedTableComponent";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetCostTrackerQuery, useLazyGetCostTrackerQuery } from "store/slices/apis/costSlice";
import { PiArrowFatRightFill, PiArrowFatUpFill } from "react-icons/pi";
import { PiArrowFatDownFill } from "react-icons/pi";
import moment from "moment";
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import * as Yup from "yup";
import { Form, Formik } from "formik";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import WbsTreeView from "view/dashboard/wbsTreeView";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TrackerPageHeader from "components/TrackerPageHeader";


const AbbreviationBadge = ({
  short,
  full,
  color = "#2563eb",
  expandable = true
}) => {

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "3px",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        // transition: "all 0.2s ease",
        // "&:hover": {
        //     boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        //     transform: "translateY(-1px)"
        // }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          // cursor: expandable ? "pointer" : "default"
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.75,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            flex: 1
          }}
        >
          <Tooltip title={full} arrow>
            <Typography sx={{ minWidth: 38, px: 0.6, py: 0.2, borderRadius: "3px", bgcolor: `${color}12`, color, border: `1px solid ${color}26`, textAlign: "center", fontWeight: 700, fontSize: 10.5, lineHeight: "15px" }}>
              {short}
            </Typography>
          </Tooltip>

          <Typography sx={{ minWidth: 0, fontSize: 11, lineHeight: "15px", color: "#64748B", whiteSpace: "normal", overflowWrap: "anywhere" }}>
            {full}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const abbreviations = [
  { short: "PV", full: "Planned value", color: "#00344C" },
  { short: "AC", full: "Actual cost", color: "#00344C" },
  { short: "BAC", full: "Budget at completion", color: "#00344C" },
  { short: "BCWS", full: "Budgeted cost of work scheduled", color: "#00344C" },
  { short: "EV", full: "Earned value", color: "#00344C" },
  { short: "CV", full: "Cost variance", color: "#00344C" },
  { short: "EAC", full: "Estimated at completion", color: "#00344C" },
  { short: "BCWP", full: "Budgeted cost of work performed", color: "#00344C" },
  { short: "SV", full: "Schedule variance", color: "#00344C" },
  { short: "CPI", full: "Cost performance index", color: "#00344C" },
  { short: "ETC", full: "Estimated cost to complete", color: "#00344C" },
  { short: "ACWP", full: "Actual cost of work performed", color: "#00344C" },
  { short: "SPI", full: "Schedule performance index", color: "#00344C" },

  // { short: "HVAC", full: "Heating Ventilation and Air Conditioning", color: "#2563eb" },
  // { short: "BOQ", full: "Bill of Quantity", color: "#16a34a" },
  // { short: "MEP", full: "Mechanical Electrical Plumbing", color: "#f59e0b" },
  // { short: "BMS", full: "Building Management System", color: "#9333ea" },
  // { short: "ELV", full: "Extra Low Voltage", color: "#ef4444" }
];

const validationSchema = Yup.object({
  wbsId: Yup.string().nullable(),
  wbsName: Yup.string().nullable(),
});

const formatKey = (str) => str.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");

const formatDate = (date) => {
  if (!date) return "";
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format("DD/MM/YYYY") : "";
};

const Header = ({ title, subtitle, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      textAlign: align,
      display: "flex",
      flexDirection: "column",
      lineHeight: 1.1,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const transformApiData = (apiData) => {
  console.log("apiData", apiData);
  return apiData?.items?.map((item) => {
    let flattenedRow = {
      id: item.activityId,
      activity: item.activity,
      bac: item.bac,
      bcws: item.bcws,
      bcwp: item.bcwp,
      ac: item.ac,
      sv: item.sv,
      spi: item.spi,
      acwp: item.acwp,
      cpi: item.cpi,
      cv: item.cv,
      etC_wrt_BAC: item.etC_wrt_BAC,
      committedCost: item.committedCost,
      etC_wrt_Committed: item.etC_wrt_Committed,
      eac: item.eac,
      parentTaskId: item.parentTaskId,
      costVariance_BAC_EA: item.costVariance_BAC_EA,
      pctVariance_wrt_BAC: item.pctVariance_wrt_BAC,
      remarks: item.remarks,
    };

    flattenedRow.children = [];

    // if (item.isLeaf === false) {
    //   flattenedRow.hasChildren = true;
    // }
    flattenedRow.hasChildren = true;
    return flattenedRow;
  });
};



const CostTracker = () => {
  const { projectId } = useParams();
  const [initialData, setInitialData] = useState([]);
  const [
    trigger,
    { data: lazyData, isFetching: lazyLoading, isError: lazyError },
  ] = useLazyGetCostTrackerQuery();

  const { data, isFetching: isLoading, isError } = useGetCostTrackerQuery({ projectId });
  const [loaderId, setLoaderId] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isApplyFilter, setIsApplyFIlter] = React.useState(false);
  const [showSummary, setShowSummary] = useState(true);

  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef(null);
  const arrowRef = React.useRef(null);

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [filterTableColumns, setTableColumns] = useState([]);
  const [columnAnchor, setColumnAnchor] = useState(null);

  const [filters, setFilters] = useState({
    projectId: projectId,
    wbsName: "",
    parentId: ""
  });

  const handleColumnOpen = (e) => setColumnAnchor(e.currentTarget);
  const handleColumnClose = () => setColumnAnchor(null);


  const tableColumns = useMemo(() => {
    const cols = [
      {
        name: <Header title="Activity" align="left" />,
        field: "activity",
        sticky: true,
        width: 400,
        align: "left",
        renderer: (row) => (
          row?.activity ?? "N/A"
        ),
      },
      {
        name: <Header title="BAC" subtitle={"(INR Cr.)"} />,
        field: "bac",
        width: 80,
        renderer: (row) => (
          row?.bac ?? "N/A"
        ),
      },
      {
        name: <Header title="Committed Cost" subtitle={"(INR Cr.)"} />,
        field: "committedCost",
        width: 80,
        renderer: (row) => (
          row?.committedCost ?? "N/A"
        ),
      },
      {
        name: <Header title="BCWS (PV)" subtitle={"(INR Cr.)"} />,
        field: "bcws",
        width: 80,
        renderer: (row) => (
          row?.bcws ?? "N/A"
        ),
      },
      {
        name: <Header title="BCWP (EV)" subtitle={"(INR Cr.)"} />,
        field: "bcwp",
        width: 80,
        renderer: (row) => (
          row?.bcwp ?? "N/A"
        ),
      },
      {
        name: <Header title="SV (EV-PV)" subtitle={"(INR Cr.)"} />,
        field: "sv",
        width: 80,
        renderer: (row) => (
          row?.sv ?? "N/A"
        ),
      },
      {
        name: <Header title="SPI (EV/PV)" />,
        field: "spi",
        width: 80,
        renderer: (row) => (
          row?.spi ?? "N/A"
        ),
      },
      {
        name: <Header title="ACWP" subtitle={"(INR Cr.)"} />,
        field: "ac",
        width: 80,
        renderer: (row) => (
          row?.ac ?? "N/A"
        ),
      },
      {
        name: <Header title="CV (EV-AC)" subtitle={"(INR Cr.)"} />,
        field: "cv",
        width: 80,
        renderer: (row) => (
          row?.cv ?? "N/A"
        ),
      },
      {
        name: <Header title="CPI (EV/AC)" />,
        field: "cpi",
        width: 80,
        renderer: (row) => (
          row?.cpi ?? "N/A"
        ),
      },
      {
        name: <Header title="ETC wrt BAC" subtitle={"(INR Cr.)"} />,
        field: "etC_wrt_BAC",
        width: 80,
        renderer: (row) => (
          row?.etC_wrt_BAC ?? "N/A"
        ),
      },
      {
        name: <Header title="ETC wrt Cost Committed" subtitle={"(INR Cr.)"} />,
        field: "etC_wrt_Committed",
        width: 80,
        renderer: (row) => (
          row?.etC_wrt_Committed ?? "N/A"
        ),
      },
      {
        name: <Header title="EAC" subtitle={"(INR Cr.)"} />,
        field: "eac",
        width: 80,
        renderer: (row) => (
          row?.eac ?? "N/A"
        ),
      },
      // {
      //   name: "Remarks",
      //   width: 100,
      //   field: "remarks",
      //   renderer: (row) => (
      //     row?.remarks ?? "N/A"
      //   ),
      // },

    ];
    return cols;
  }, []);

  useEffect(() => {
    if (data?.data) {
      const transformedData = transformApiData(data.data);
      setInitialData(transformedData);
    }
  }, [data]);

  useEffect(() => {
    const allColumns = tableColumns;
    setVisibleColumns(allColumns.map(col => col.field));
  }, []);

  const insertChildByParentId = (prevData, parentId, newData) => {
    return prevData.map((item) => {
      if (item.id === parentId) {
        return { ...item, children: newData, hasChildren: newData?.length > 0 ? true : false };
      }
      if (item.children?.length > 0) {
        return {
          ...item,
          children: insertChildByParentId(item.children, parentId, newData),
        };
      }
      return item;
    });
  };

  const handleClick = async (row) => {
    console.log("clickedData", row);
    if (row.children.length > 0) {
      return;
    }

    try {
      setIsFiltering(false)
      const parentId = row.id;
      setLoaderId(parentId);
      const result = await trigger({
        projectId: projectId,
        parentId: parentId,
      }).unwrap();
      console.log("Result", result);
      const childrens = transformApiData(result?.data) || [];

      const updatedData = insertChildByParentId(
        initialData,
        parentId,
        childrens
      );
      console.log("updated data", updatedData);
      setInitialData(updatedData);
    } catch (error) {
      console.log("Lazy Load Error:", error);
    } finally {
      setLoaderId(null);
    }
  };

  const handleApplyFilters = async (values, { setSubmitting }) => {
    console.log("Apply filters →", values);
    setIsFiltering(true)
    const payload = {
      projectId: projectId,
      activityId: values.wbsId || ""
    };

    // keep name for showing in Chip / text
    const newFilters = {
      ...payload,
      wbsName: values.wbsName || ""
    };

    console.log("New filters →", newFilters);
    setFilters(newFilters);

    // call your APIs
    await trigger(payload);

    setIsApplyFIlter(true);
    setOpen(false);
    setSubmitting(false);
  };

  const handleReset = (resetForm) => {
    resetForm();
    resetAllFilters();
  };

  const resetAllFilters = () => {
    setIsFiltering(true)
    const resetValues = {
      projectId,
      wbsId: "",
      wbsName: "",
      activityId: ""
    };

    // update local filters state
    setFilters(resetValues);

    // reload APIs with empty filters
    trigger(resetValues);

    setIsApplyFIlter(false);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (lazyData?.data && isFiltering) {
      const transformedFilteredData = transformApiData(lazyData.data);
      setInitialData(transformedFilteredData);
      setIsFiltering(false)
    }
  }, [lazyData]);

  const handleToggleSummary = () => {
    setShowSummary((prev) => !prev);
  }

  return (
    <>
    <TrackerPageHeader
      title="Cost Tracker"
      description="Review earned value, cost performance, and forecast metrics."
    />
    <Grid
      container
      spacing={2}
      sx={{ width: "100%", p: 2, boxSizing: "border-box" }}
    >
      <Grid size={12}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: "6px",
        }}>
          <Stack direction="row" spacing={2}>
            {filters.wbsName && (
              <Chip
                label={
                  <Typography variant="body2">
                    <strong style={{ color: "#0E73F6" }}>Filter:</strong>{" "}
                    <span style={{ fontWeight: 400, color: "#6B7280" }}>
                      {filters.wbsName}
                    </span>
                  </Typography>
                }
                sx={{
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: 0
                }}
                variant="outlined"
                onDelete={resetAllFilters}
              />
            )}

            <IconButton aria-label="delete" size="large"
              ref={anchorRef}
              onClick={handleToggle}
            >
              <FilterAltOutlinedIcon fontSize="medium" />
            </IconButton>


            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-end"
              transition
              disablePortal={false}
               sx={{ zIndex: 4000 }}
              onClick={(e) => e.stopPropagation()}
              modifiers={[
                {
                  name: 'arrow',
                  enabled: true,
                  options: {
                    element: arrowRef?.current,
                  },
                },
              ]}
              // sx={{ zIndex: 20 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    sx={{
                      p: 2,
                      // borderRadius: 1,
                      boxShadow: "none",
                      width: 300,
                      borderRadius: 3,
                      border: "2px solid #eef0f4",
                      position: 'relative'
                    }}
                  >
                    <ArrowDropUpRoundedIcon
                      ref={arrowRef}
                      sx={{
                        position: "absolute",
                        top: "-33px",
                        right: "0px",
                        fontSize: "60px",
                        color: "white",
                        filter: "drop-shadow(0px -1px 1px rgba(0,0,0,0.15))",
                      }}
                    />
                    <ClickAwayListener onClickAway={handleClose}>
                      <Box>
                        {open && (

                          <Formik
                            initialValues={filters}
                            validationSchema={validationSchema}
                            onSubmit={handleApplyFilters}
                            enableReinitialize
                          >
                            {({ values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                              handleSubmit,
                              isSubmitting,
                              setFieldValue,
                              resetForm,
                              setFieldTouched,
                              setTouched }) => {
                              // const subCatOptions = values?.category
                              //     ? subCategoryMap[values?.category]
                              //     : [];

                              return (
                                <Form>
                                  <Grid container spacing={2}>
                                    {/* ... your Category + Sub Category stuff ... */}

                                    {/* WBS TREE SECTION */}
                                    <Grid size={{ xs: 12, md: 12 }}>
                                      <Typography sx={{ mb: 1, mt: 1 }}>
                                        WBS Selector
                                      </Typography>

                                      <WbsTreeView
                                        projectId={projectId}
                                        onSelect={function (node) {
                                          // node = { id, name, raw, isLeaf }

                                          // Save into form values
                                          setFieldValue("wbsId", node.id);
                                          setFieldValue("wbsName", node.name);
                                        }}
                                      />

                                      {/* Optional: show selected WBS name inside popup */}
                                      {values.wbsName && (
                                        <Typography sx={{ mt: 1, fontSize: 12, color: "#555" }}>
                                          Selected WBS: <strong>{values.wbsName}</strong>
                                        </Typography>
                                      )}
                                    </Grid>

                                    {/* Apply */}
                                    <Grid size={12}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          alignItems: "center"
                                        }}
                                      >
                                        {isApplyFilter && (
                                          <Button
                                            onClick={function () {
                                              handleReset(resetForm);
                                            }}
                                            sx={{
                                              textTransform: "none",
                                              color: "#F15744",
                                              fontSize: "14px",
                                              lineHeight: "100%",
                                              fontFamily: "Outfit, sans-serif",
                                              borderRadius: "3px",
                                              fontWeight: 500,
                                              px: "20px",
                                              py: "15px",
                                            }}
                                          >
                                            Reset
                                          </Button>
                                        )}

                                        <Button
                                          type="submit"
                                          variant="contained"
                                          disabled={isSubmitting}
                                          sx={{
                                            backgroundColor: "#00344C",
                                            borderRadius: "3px",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            fontSize: "14px",
                                            lineHeight: "100%",
                                            fontFamily: "Outfit, sans-serif",
                                            color: "#FFFFFF",
                                            px: "20px",
                                            py: "15px",
                                          }}
                                        >
                                          Apply
                                        </Button>
                                      </Box>
                                    </Grid>
                                  </Grid>

                                </Form>
                              );
                            }}
                          </Formik>

                        )}
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Stack>
        </Box>
        {data?.data?.statusDate && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              bgcolor: "#F3F4F6",
              borderRadius: "3px",
              border: "1px solid #E5E7EB"
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#6B7280",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}
            >
              Status as on:
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#111827",
                fontWeight: 700
              }}
            >
              {formatDate(data?.data?.statusDate) ?? "N/A"}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid size={12}>
        <Button
          variant="text"
          onClick={handleToggleSummary}
          sx={{
            fontFamily: "Outfit, sans-serif",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            minHeight: 32,
            px: 1.25,
            color: "#475569",
            border: "1px solid #E2E8F0",
            borderRadius: "3px",
          }}
        >
          {showSummary ? "Hide legend" : "Show legend"}
        </Button>
      </Grid>
      {showSummary &&
        <Grid size={12}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", border: "1px solid #E2E8F0", borderRadius: "3px", bgcolor: "#FFFFFF", overflow: "hidden", "& > *": { minHeight: 46, borderRight: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0" } }}>
            {abbreviations.map((item) => <AbbreviationBadge key={item.short} {...item} />)}
          </Box>
        </Grid>
      }
      <Grid size={12}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { md: "row", xs: "column" },
            alignItems: { md: "center", xs: "flex-start" },
            justifyContent: 'end',
            p: "8px 0px"
          }}
        >
          <Popover
            open={Boolean(columnAnchor)}
            anchorEl={columnAnchor}
            onClose={handleColumnClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, width: 300 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Select Columns
              </Typography>
              {tableColumns.map((col) => (
                <Box key={col.field} sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={visibleColumns.includes(col.field)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibleColumns(prev => [...prev, col.field]);
                      } else {
                        setVisibleColumns(prev => prev.filter(f => f !== col.field));
                      }
                    }}
                  />
                  <Typography sx={{ fontSize: 13 }}>
                    {col.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>

          <IconButton onClick={handleColumnOpen}>
            <ViewColumnIcon size={24} color="#48535B" />
          </IconButton>
        </Box>
      </Grid>
      <Grid size={12} sx={{ mt: 1, position: "relative", minHeight: "400px" }}>
        {(isLoading || (isFiltering && lazyLoading)) && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
          }}>
            <CircularProgress />
          </Box>
        )}
        {(isError || lazyError) && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'red' }}>
            <Typography variant="h6">Error loading data.</Typography>
          </Box>
        )}
        <Box sx={{
          opacity: (isLoading || (isFiltering && lazyLoading)) ? 0.5 : 1,
          pointerEvents: (isLoading || (isFiltering && lazyLoading)) ? 'none' : 'auto',
          transition: 'opacity 0.2s ease'
        }}>
          {!isError && (
            <NestedTableComponent
              data={initialData}
              columns={tableColumns.filter(col => visibleColumns.includes(col.field))}
              //months={[]}
              handleClick={handleClick}
              loaderId={loaderId}
            />
          )}
        </Box>
      </Grid>
    </Grid>
    </>
  );
};

export default CostTracker;
